"use client";

import { motion } from "framer-motion";
import {
  MousePointerClick,
  Play,
  Sparkles,
  User as UserIcon,
} from "lucide-react";
import { cn } from "@/lib/utils/utils";
import type { CopilotMessage as Msg } from "@/lib/copilot/types";
import { CopilotToolCallCard } from "./CopilotToolCallCard";
import { CopilotMarkdown } from "./CopilotMarkdown";
import { useCopilot } from "@/lib/copilot/useCopilot";
import { FakiLoader, FakiMark } from "./FakiMark";

export function CopilotMessage({ msg }: { msg: Msg }) {
  const isUser = msg.role === "user";
  const hasToolCalls = !!msg.toolCalls && msg.toolCalls.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
      className={cn(
        "flex w-full gap-2",
        isUser ? "justify-end" : "justify-start",
      )}
    >
      {!isUser && (
        <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/10 p-1 text-primary">
          <FakiMark className="h-full w-full object-contain" />
        </div>
      )}
      <div
        className={cn(
          "flex min-w-0 max-w-[85%] flex-col gap-1.5",
          isUser ? "items-end" : "items-start",
        )}
      >
        {hasToolCalls && (
          <div className="flex w-full flex-col gap-1">
            {msg.toolCalls!.map((tc) => (
              <CopilotToolCallCard key={tc.id} tc={tc} />
            ))}
          </div>
        )}

        {(msg.content || msg.error || (msg.pending && !hasToolCalls)) && (
          <div
            className={cn(
              "w-full rounded-2xl px-3.5 py-2 text-[13px] leading-relaxed shadow-sm",
              isUser
                ? "rounded-tr-sm bg-primary text-primary-foreground"
                : "rounded-tl-sm bg-muted text-foreground",
              msg.error &&
                "border border-destructive/40 bg-destructive/5 text-destructive",
            )}
          >
            {msg.pending && !msg.content ? (
              <TypingDots />
            ) : (
              <>
                {msg.content && (
                  <CopilotMarkdown inverse={isUser}>
                    {msg.content}
                  </CopilotMarkdown>
                )}
                {msg.error && (
                  <p className="mt-1 text-[11px] font-medium opacity-80">
                    {msg.error}
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {msg.followSuggestion && (
          <FollowSuggestionBanner suggestion={msg.followSuggestion} />
        )}

        {msg.continuePrompt && <ContinueButton />}
      </div>
      {isUser && (
        <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
          <UserIcon className="h-3.5 w-3.5" />
        </div>
      )}
    </motion.div>
  );
}

function ContinueButton() {
  const { send, isSending } = useCopilot();
  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex w-full items-center justify-between gap-2 rounded-xl border border-amber-500/30 bg-amber-500/5 px-3 py-2.5"
    >
      <div className="min-w-0 flex-1">
        <p className="text-[12px] font-semibold leading-tight text-amber-700 dark:text-amber-500">
          ¿Continuar con la tarea?
        </p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          Llegué al máximo de pasos automáticos. Si quieres que siga, dale click.
        </p>
      </div>
      <button
        type="button"
        disabled={isSending}
        onClick={() => void send("Continúa con la tarea original.")}
        className="flex flex-shrink-0 cursor-pointer items-center gap-1 rounded-full bg-amber-500 px-2.5 py-1 text-[11px] font-medium text-white transition hover:bg-amber-600 disabled:opacity-50"
      >
        <Play className="h-3 w-3" />
        Continuar
      </button>
    </motion.div>
  );
}

function FollowSuggestionBanner({
  suggestion,
}: {
  suggestion: NonNullable<Msg["followSuggestion"]>;
}) {
  const { updateSettings, send } = useCopilot();

  const enableAndReplay = () => {
    updateSettings({ mode: "follow" });
    // Pequeño delay para que el setting se propague antes de re-disparar
    setTimeout(() => {
      void send(`Repíteme la guía: ${suggestion.title}`);
    }, 250);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex w-full items-start gap-2 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2.5"
    >
      <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
        <MousePointerClick className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[12px] font-semibold leading-tight">
          ¿Te lo muestro visualmente?
        </p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          Activa el modo guía visual para ver cada paso con un cursor virtual.
        </p>
      </div>
      <button
        type="button"
        onClick={enableAndReplay}
        className="flex flex-shrink-0 cursor-pointer items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-[11px] font-medium text-primary-foreground transition hover:bg-primary/90"
      >
        <Sparkles className="h-3 w-3" />
        Activar y mostrar
      </button>
    </motion.div>
  );
}

export function TypingDots() {
  return (
    <div className="flex items-center gap-1.5 py-0.5">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="block h-1.5 w-1.5 rounded-full bg-muted-foreground/60"
          animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
          transition={{
            duration: 1.0,
            repeat: Infinity,
            delay: i * 0.15,
            ease: "easeInOut",
          }}
        />
      ))}
      <FakiLoader className="ml-1 h-3.5 w-3.5" />
    </div>
  );
}
